import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  PaginatedResult,
  PaginationFilter,
} from '../../shared/models/pagination.model';
import { Product } from '../../shared/models/product.model';
import { Observable } from 'rxjs';
import { CrudService } from '../../shared/models/crud-service.model';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ProductsService implements CrudService<Product> {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.backendUrl + 'products';

  loadWithFilter(
    filter: Partial<PaginationFilter>
  ): Observable<PaginatedResult<Product>> {
    try {
      let httpParams = new HttpParams()
        .set('page', (filter?.page ?? 0).toString())
        .set('limit', (filter?.limit ?? 20).toString());

      if (filter.search) {
        httpParams = httpParams.set('search', filter.search);
      }

      if (filter.isVerified !== undefined) {
        httpParams = httpParams.set('isVerified', filter.isVerified);
      }

      return this.http.get<PaginatedResult<Product>>(this.apiUrl, {
        params: httpParams,
      });
    } catch (error) {
      console.error('Error loading products:', error);
      throw error;
    }
  }

  loadById(id: string): Observable<Product> {
    try {
      return this.http.get<Product>(`${this.apiUrl}/${id}`);
    } catch (error) {
      console.error('Error loading product by id:', error);
      throw error;
    }
  }

  create(entity: Omit<Product, 'id'>): Observable<Product> {
    try {
      return this.http.post<Product>(this.apiUrl, entity);
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  }

  update(id: string, entity: Product): Observable<Product> {
    try {
      if (!id) {
        throw new Error('Product id is required for update');
      }
      return this.http.patch<Product>(`${this.apiUrl}/${id}`, entity);
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  }

  delete(id: string): Observable<void> {
    try {
      return this.http.delete<void>(`${this.apiUrl}/${id}`);
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  }

  deleteMany(ids: string[]): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}`, {
      body: {
        ids,
      },
    });
  }

  getTotalCount(): Observable<{ total: number }> {
    return this.http.get<{ total: number }>(`${this.apiUrl}/count`);
  }
}
